import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { ConfigApi } from '@eis/insuranceservice-sdk';
import { useApiClients } from './ApiClientsProvider';
import { apiKeys } from './keys';
import { unwrap } from './result';
import { useServiceMutation, type ServiceMutation } from './useServiceMutation';

/**
 * The version lifecycle (SDD-016 §4): DRAFT versions and the status machine, for a tarif AND a
 * group tarif. One hook per operation with an `owner` switch, so the two sides cannot drift — the
 * contract is identical on both (`CreateVersion` ‖ `CreateGroupVersion`, …).
 *
 * The preconditions ride on the SDK's ETag middleware, as everywhere: a DRAFT's `…/versions/dv-…`
 * read parks the draft's ETag (its `draft_version`); a PUBLISHED version carries none, so a status
 * change on it inherits the GENERATION's by walking up the path — which is exactly the mutable spot
 * an activation or a retirement moves (SDD-016 DEV-17).
 */

export type VersionOwner = 'tarif' | 'group';

/** A version as both services return it — the two messages carry the same fields. */
export type LifecycleVersion = ConfigApi.TarifVersion;

export type LifecycleStatus =
  | 'VERSION_STATUS_PUBLISHED'
  | 'VERSION_STATUS_ACTIVE'
  | 'VERSION_STATUS_RETIRED'
  | 'VERSION_STATUS_DISCARDED';

interface Address {
  readonly owner: VersionOwner;
  readonly slug: string;
  readonly generation: string;
}

const versionsKey = ({ owner, slug, generation }: Address) =>
  owner === 'tarif'
    ? apiKeys.config.versions(slug, generation)
    : apiKeys.config.groupVersions(slug, generation);

const generationKey = ({ owner, slug, generation }: Address) =>
  owner === 'tarif'
    ? apiKeys.config.generation(slug, generation)
    : apiKeys.config.groupGeneration(slug, generation);

/**
 * Every version of a generation INCLUDING its open drafts (D-V10: the list shows DRAFT rows with
 * their issue counts). Keyed under the plain versions key, so every invalidation of the list
 * reaches it too; the plain `useTarifVersions` keeps its published-only answer for the pickers.
 */
export function useVersionsWithDrafts(address: Address): UseQueryResult<readonly LifecycleVersion[]> {
  const { config } = useApiClients();
  const { owner, slug, generation } = address;
  return useQuery({
    queryKey: [...versionsKey(address), 'with-drafts'] as const,
    queryFn: async (): Promise<readonly LifecycleVersion[]> => {
      if (owner === 'tarif') {
        const response = await unwrap(
          config.client.GET('/v1/tarifs/{tarifSlug}/generations/{generation}/versions', {
            params: { path: { tarifSlug: slug, generation }, query: { includeDrafts: true } },
          }),
        );
        return response.versions ?? [];
      }
      const response = await unwrap(
        config.client.GET('/v1/group-tarifs/{groupTarifSlug}/generations/{generation}/versions', {
          params: { path: { groupTarifSlug: slug, generation }, query: { includeDrafts: true } },
        }),
      );
      return (response.versions ?? []);
    },
  });
}

/** One draft, read so its ETag is parked for the edit that follows. */
export function useDraftVersion(
  address: Address & { readonly draftId: string | undefined },
): UseQueryResult<LifecycleVersion> {
  const { config } = useApiClients();
  const { owner, slug, generation, draftId } = address;
  return useQuery({
    queryKey: [...versionsKey(address), 'draft', draftId ?? ''] as const,
    enabled: draftId !== undefined && draftId !== '',
    queryFn: async (): Promise<LifecycleVersion> => {
      const version = draftId ?? '';
      if (owner === 'tarif') {
        const response = await unwrap(
          config.client.GET('/v1/tarifs/{tarifSlug}/generations/{generation}/versions/{version}', {
            params: { path: { tarifSlug: slug, generation, version } },
          }),
        );
        return response.tarifVersion ?? {};
      }
      const response = await unwrap(
        config.client.GET('/v1/group-tarifs/{groupTarifSlug}/generations/{generation}/versions/{version}', {
          params: { path: { groupTarifSlug: slug, generation, version } },
        }),
      );
      return (response.groupTarifVersion ?? {});
    },
  });
}

export interface CreateDraftVariables extends Address {
  /** The version whose manifest seeds the draft and becomes its `basedOn`. */
  readonly basedOnErn?: string;
  /** An incomplete manifest, stored as given with its issues (D-V4). */
  readonly manifest?: Record<string, unknown>;
}

/** Stores a DRAFT version — incomplete is allowed; the issues come back instead of a refusal. */
export function useCreateDraftVersion(): ServiceMutation<CreateDraftVariables, LifecycleVersion> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ owner, slug, generation, basedOnErn, manifest }: CreateDraftVariables) => {
      const body = {
        ...(basedOnErn === undefined ? {} : { basedOnErn }),
        ...(manifest === undefined ? {} : { manifest }),
      };
      if (owner === 'tarif') {
        const response = await unwrap(
          config.client.POST('/v1/tarifs/{tarifSlug}/generations/{generation}/versions', {
            params: { path: { tarifSlug: slug, generation } },
            body,
          }),
        );
        return response.version ?? {};
      }
      const response = await unwrap(
        config.client.POST('/v1/group-tarifs/{groupTarifSlug}/generations/{generation}/versions', {
          params: { path: { groupTarifSlug: slug, generation } },
          body,
        }),
      );
      return (response.version ?? {});
    },
    invalidates: (variables) => [versionsKey(variables)],
  });
}

export interface UpdateDraftVariables extends Address {
  readonly draftId: string;
  readonly manifest: Record<string, unknown>;
}

/** Rewrites a DRAFT under its ETag; the issues are recomputed server-side. */
export function useUpdateDraftVersion(): ServiceMutation<UpdateDraftVariables, LifecycleVersion> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ owner, slug, generation, draftId, manifest }: UpdateDraftVariables) => {
      if (owner === 'tarif') {
        const response = await unwrap(
          config.client.PATCH('/v1/tarifs/{tarifSlug}/generations/{generation}/versions/{version}', {
            params: { path: { tarifSlug: slug, generation, version: draftId } },
            body: { manifest },
          }),
        );
        return response.version ?? {};
      }
      const response = await unwrap(
        config.client.PATCH('/v1/group-tarifs/{groupTarifSlug}/generations/{generation}/versions/{version}', {
          params: { path: { groupTarifSlug: slug, generation, version: draftId } },
          body: { manifest },
        }),
      );
      return (response.version ?? {});
    },
    // The draft (re-read restores its ETag for the next save) and the list (issue counts).
    invalidates: (variables) => [versionsKey(variables)],
  });
}

export interface SetVersionStatusVariables extends Address {
  /** A number (`1.2`), a version ERN, or a draft id (`dv-…`). */
  readonly version: string;
  readonly status: LifecycleStatus;
  /** DRAFT → PUBLISHED only: ask for a major whatever the golden delta says (D-V2). */
  readonly requestMajor?: boolean;
}

export type SetVersionStatusResult =
  | ConfigApi.SetVersionStatusResponse
  | ConfigApi.SetGroupVersionStatusResponse;

/**
 * One move through the status machine (D-V3): publish a draft, activate, retire, discard. No
 * in-force instance moves — which the confirmation dialog says, because it is the fear that stops
 * a rollback (SDD-005 §7).
 */
export function useSetVersionStatus(): ServiceMutation<SetVersionStatusVariables, SetVersionStatusResult> {
  const { config } = useApiClients();
  return useServiceMutation({
    mutationFn: async ({ owner, slug, generation, version, status, requestMajor }: SetVersionStatusVariables) => {
      const body = { status, ...(requestMajor === true ? { requestMajor: true } : {}) };
      if (owner === 'tarif') {
        return unwrap(
          config.client.POST('/v1/tarifs/{tarifSlug}/generations/{generation}/versions/{version}:setStatus', {
            params: { path: { tarifSlug: slug, generation, version } },
            body,
          }),
        );
      }
      return unwrap(
        config.client.POST(
          '/v1/group-tarifs/{groupTarifSlug}/generations/{generation}/versions/{version}:setStatus',
          { params: { path: { groupTarifSlug: slug, generation, version } }, body },
        ),
      );
    },
    // The pointer and the version set moved; the generation re-read restores its ETag too.
    invalidates: (variables) => [generationKey(variables), versionsKey(variables)],
  });
}
